var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/** @jsx jsx */
import { css, jsx, React, classNames, ReactRedux, ReactResizeDetector, AppMode, ImageDisplayQualityMode } from 'jimu-core';
import { getCroppedImgPositionInWidget, isIllegalUrl, updateUrlByDisplayQualityMode, canUseImageDisplayQuality, canLoadFuzzyImage, getFuzzyImageWidth } from './util';
import { withTheme } from 'jimu-theme';
const imageSizeMap = {};
/**
 * The fill mode for image.
 */
export var ImageFillMode;
(function (ImageFillMode) {
    ImageFillMode["Fit"] = "FIT";
    ImageFillMode["Fill"] = "FILL";
})(ImageFillMode || (ImageFillMode = {}));
/** @ignore */
export var ImgSourceType;
(function (ImgSourceType) {
    ImgSourceType["ByURL"] = "BY_URL";
    ImgSourceType["ByUpload"] = "BY_UPLOAD";
})(ImgSourceType || (ImgSourceType = {}));
var ImgLoadState;
(function (ImgLoadState) {
    ImgLoadState["Loading"] = "LOADING";
    ImgLoadState["LoadOk"] = "LOADOK";
    ImgLoadState["LoadError"] = "LOADERROR";
})(ImgLoadState || (ImgLoadState = {}));
/** @ignore */
export var CropType;
(function (CropType) {
    CropType["Real"] = "REAL";
    CropType["Fake"] = "FAKE";
})(CropType || (CropType = {}));
/** @ignore */
export class _ImageWithParam extends React.PureComponent {
    constructor(props) {
        super(props);
        this.__unmount = false;
        this.rootRef = React.createRef();
        this.getAdaptiveUrlFromImageParam = (imageParam) => {
            const url = imageParam === null || imageParam === void 0 ? void 0 : imageParam.url;
            if (isIllegalUrl(url)) { // verification here because use this method both in render() and didMount()/didUpdate()
                // console.error('Image: illegal url: ' + url)
                return null; // illegal url
            }
            if (!(imageParam === null || imageParam === void 0 ? void 0 : imageParam.isUseCompress)) {
                return url;
            }
            else {
                const isBlobCacheResourceExp = new RegExp('^blob:\http(s?)://(.)');
                if (isBlobCacheResourceExp.test(imageParam.url)) {
                    return url; // imageParam.url
                }
                if (imageParam.imgSourceType === ImgSourceType.ByUpload) {
                    return url.replace(imageParam.fileName, imageParam.fileName.split('.')[0] + '_compress.' + imageParam.fileName.split('.')[1]);
                }
                return url;
            }
        };
        this.preLoadCommonImage = (imageParam, mode) => __awaiter(this, void 0, void 0, function* () {
            const url = yield this.parseImageSrc(imageParam, mode);
            const { width, height } = imageSizeMap[url] || {};
            if (width && height) {
                this.setState({
                    imageWidth: width,
                    imageHeight: height
                });
            }
            this.imageObject = new Image();
            this.imageObject.src = url;
            this.imageObject.onload = () => {
                if (!this.imageObject || this.imageParam !== imageParam || this.__unmount) {
                    return;
                }
                const { width, height } = this.imageObject;
                imageSizeMap[url] = { width, height };
                this.setState({
                    picLoadResult: ImgLoadState.LoadOk,
                    imageWidth: width,
                    imageHeight: height,
                    imageSrc: url
                });
            };
            this.imageObject.onerror = () => {
                if (!this.imageObject || this.imageParam !== imageParam || this.__unmount) {
                    return;
                }
                this.setState({
                    picLoadResult: ImgLoadState.LoadError,
                    imageWidth: null,
                    imageHeight: null,
                    imageSrc: ''
                });
            };
        });
        this.preLoadImage = (imageParam, mode) => __awaiter(this, void 0, void 0, function* () {
            this.cancelPreLoadImage();
            this.imageParam = imageParam;
            if (isIllegalUrl(imageParam.url)) {
                this.setState({
                    picLoadResult: ImgLoadState.LoadError,
                    imageWidth: null,
                    imageHeight: null,
                    imageSrc: ''
                });
                return;
            }
            if (canLoadFuzzyImage(imageParam) && mode !== ImageDisplayQualityMode.Fuzzy) {
                const url = yield this.parseImageSrc(imageParam, ImageDisplayQualityMode.Fuzzy);
                this.setState({
                    picLoadResult: ImgLoadState.Loading,
                    imageWidth: getFuzzyImageWidth()
                }, () => {
                    this.preLoadCommonImage(imageParam, mode).then(() => {
                        if (this.imageParam !== imageParam)
                            return;
                        this.setState({
                            imageSrc: url
                        });
                    });
                });
            }
            else {
                this.setState({
                    picLoadResult: ImgLoadState.Loading
                }, () => {
                    this.preLoadCommonImage(imageParam, mode);
                });
            }
        });
        this.cancelPreLoadImage = () => {
            if (this.imageObject)
                this.imageObject.src = '';
            this.imageObject = null;
            this.imageParam = null;
        };
        this.standardUrl = (url) => {
            if (url === '' || url === null || url === undefined) {
                return '';
            }
            else {
                return url;
            }
        };
        this.onResize = ({ width, height }) => {
            if (this.__unmount) {
                return;
            }
            this.setState({
                widgetWidth: width,
                widgetHeight: height
            });
        };
        this.getWidgetWidth = () => {
            return (this.props.size && this.props.size.width) ? this.props.size.width : this.state.widgetWidth;
        };
        this.getWidgetHeight = () => {
            return (this.props.size && this.props.size.height) ? this.props.size.height : this.state.widgetHeight;
        };
        this.parseImageSrc = (imageParam_1, ...args_1) => __awaiter(this, [imageParam_1, ...args_1], void 0, function* (imageParam, mode = ImageDisplayQualityMode.Original) {
            var _a;
            const { url, originalWidth, imgSourceType, cropParam, originalUrl = url, fileFormat = 'image/jpeg' } = imageParam;
            const cropWidth = (_a = cropParam === null || cropParam === void 0 ? void 0 : cropParam.cropPixel) === null || _a === void 0 ? void 0 : _a.width;
            // If url is illegal, callback param is null.
            if (isIllegalUrl(url)) {
                return null;
            }
            const adaptiveUrl = this.getAdaptiveUrlFromImageParam(imageParam);
            if (mode === ImageDisplayQualityMode.Original || imgSourceType === ImgSourceType.ByURL ||
                (!canLoadFuzzyImage(imageParam) && !canUseImageDisplayQuality(imageParam))) {
                return adaptiveUrl;
            }
            return yield updateUrlByDisplayQualityMode({ originalUrl, originalWidth, displayUrl: adaptiveUrl, cropWidth, mode, mimeType: fileFormat });
        });
        this.imageParam = null;
        this.imageObject = null;
        const imageSize = imageSizeMap[this.props.imageParam && this.getAdaptiveUrlFromImageParam(this.props.imageParam)];
        this.state = {
            picLoadResult: ImgLoadState.LoadError,
            widgetWidth: this.props.size && this.props.size.width,
            widgetHeight: this.props.size && this.props.size.height,
            imageWidth: imageSize && imageSize.width,
            imageHeight: imageSize && imageSize.height,
            isRefresh: false,
            imageSrc: ''
        };
    }
    getStyle() {
        const { theme, imageParam } = this.props;
        const cropParam = imageParam.cropParam;
        let maskImage = 'none';
        const hasMask = cropParam && cropParam.cropShape && cropParam.svgViewBox && cropParam.svgPath;
        if (hasMask) {
            const svg = `<svg width="100%" height="100%" viewBox="${cropParam.svgViewBox}" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <g>
          <path d="${cropParam.svgPath}" />
        </g>
      </svg>`;
            maskImage = `url("data:image/svg+xml;base64,${window.btoa(svg)}")`;
        }
        return css `
      width: 100%;
      height: 100%;
      -webkit-mask-image: ${maskImage};
      mask: ${maskImage};
      mask-image: ${maskImage};
      mask-size: 100% 100%;

      &.widget-image-default {
        background: ${theme ? theme.ref.palette.neutral[300] : '#eaeaea'}
          url(${require('./assets/defaultimg.svg')}) center center no-repeat;
        background-size: 50% 50%;
      }

      transition: filter .25s ease, opacity .25s ease;
      &.widget-image-loading {
        filter: blur(3px);
        opacity: 1;
      }
    `;
    }
    getImageStyle() {
        const { imageParam, imageFillMode, isAutoWidth, isAutoHeight } = this.props;
        const cropParam = imageParam.cropParam;
        const cropPixel = cropParam && cropParam.cropPixel;
        const cropType = cropParam && cropParam.cropType;
        const widgetWidth = this.getWidgetWidth() || this.rootRef.current.clientWidth;
        const widgetHeight = this.getWidgetHeight() || this.rootRef.current.clientWidth;
        if (!cropPixel || !cropType || cropType === CropType.Real) {
            const objectFit = (!imageFillMode || imageFillMode === ImageFillMode.Fill) ? 'cover' : 'contain';
            const width = isAutoWidth ? 'auto' : '100%';
            let height = isAutoHeight ? 'auto' : '100%';
            // workaround for a Firefox and Safari issue:
            // https://devtopia.esri.com/Beijing-R-D-Center/ExperienceBuilder/issues/17299#issuecomment-4447014
            if (isAutoWidth && !isAutoHeight) {
                height = widgetHeight + 'px';
            }
            return css `
        width: ${width};
        height: ${height};
        object-fit: ${objectFit};
        object-position: 50% 50%;
        overflow-clip-margin: unset;
      `;
        }
        else {
            const positionInWidget = getCroppedImgPositionInWidget(cropParam, widgetWidth, widgetHeight);
            const zoom = positionInWidget.zoom;
            const left = positionInWidget.x;
            const top = positionInWidget.y;
            return css `
        position: absolute;
        left: ${left}px;
        top: ${top}px;
        width: ${this.state.imageWidth * zoom}px;
        height: ${this.state.imageHeight * zoom}px;
      `;
        }
    }
    componentDidMount() {
        this.__unmount = false;
        this.preLoadImage(this.props.imageParam, this.props.imageDisplayQualityMode);
    }
    componentWillUnmount() {
        this.__unmount = true;
        this.cancelPreLoadImage();
    }
    getSnapshotBeforeUpdate(prevProps) {
        var _a, _b;
        return this.standardUrl((_a = this.props.imageParam) === null || _a === void 0 ? void 0 : _a.url) !== this.standardUrl((_b = prevProps.imageParam) === null || _b === void 0 ? void 0 : _b.url);
    }
    componentDidUpdate(prevProps, prevState, snapshot) {
        var _a, _b, _c, _d;
        if (snapshot || this.props.imageDisplayQualityMode !== prevProps.imageDisplayQualityMode) {
            this.setState({ isRefresh: true }, () => {
                this.setState({ isRefresh: false }, () => {
                    this.preLoadImage(this.props.imageParam, this.props.imageDisplayQualityMode);
                });
            });
        }
        if (((_b = (_a = this.props.imageParam) === null || _a === void 0 ? void 0 : _a.cropParam) === null || _b === void 0 ? void 0 : _b.cropShape) !== ((_d = (_c = prevProps.imageParam) === null || _c === void 0 ? void 0 : _c.cropParam) === null || _d === void 0 ? void 0 : _d.cropShape)) {
            this.forceUpdate();
        }
    }
    render() {
        const { appMode, imageParam, toolTip, altText, size } = this.props;
        const { picLoadResult, imageSrc } = this.state;
        const showDefault = (appMode === AppMode.Design && picLoadResult === ImgLoadState.LoadError && !imageSrc) || !(imageParam === null || imageParam === void 0 ? void 0 : imageParam.url);
        const showLoading = picLoadResult === ImgLoadState.Loading && this.props.useFadein;
        return jsx("div", { css: this.getStyle(), className: classNames({
                'widget-image-default': showDefault,
                'widget-image-loading': showLoading
            }), ref: this.rootRef },
            imageSrc && jsx("img", { title: toolTip, src: imageSrc, alt: altText, css: this.getImageStyle() }),
            (!size || (!size.width && !size.height)) && jsx(ReactResizeDetector, { targetRef: this.rootRef, handleWidth: true, handleHeight: true, onResize: this.onResize }));
    }
}
_ImageWithParam.defaultProps = {
    imageParam: {}
};
const mapStateToProps = (state, ownProps) => {
    var _a, _b;
    // map appPath and queryObject to render for updating maskUrl
    const cropParam = ownProps.imageParam && ownProps.imageParam.cropParam;
    if (cropParam && (cropParam.cropType === CropType.Fake || cropParam.cropShape) && ownProps.isAutoHeight) {
        return {
            appPath: state.appPath,
            queryObject: state.queryObject,
            appMode: (_a = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _a === void 0 ? void 0 : _a.appMode
        };
    }
    else {
        return {
            appPath: null,
            queryObject: null,
            appMode: (_b = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _b === void 0 ? void 0 : _b.appMode
        };
    }
};
/**
 * The ImageWithParam component is used to visualize the image, it provides fill mode.
 * It can be used at runtime or in the setting page.
 * It can cooperate with ImageSelector component(jimu-ui/advanced/resource-selector).
 */
export const ImageWithParam = ReactRedux.connect(mapStateToProps)(withTheme(_ImageWithParam));
